import { getSerialNumberFormatError } from "./serialNumberValidation";

export const BARCODE_NOT_RECOGNIZED_TITLE = "Barcode not recognized";
export const BARCODE_NOT_RECOGNIZED_MESSAGE =
  "The code does not match equipment available for this request path.";
export const BARCODE_NO_SUB_CATEGORY_MESSAGE = "Select an equipment sub-category before scanning.";

/** A code can embed a serial as `SN|<serial>`, `SERIAL|<serial>` or `SN:<serial>`, e.g. `NAV|LBDJ901291|SN|7X-8200001`. */
const EMBEDDED_SERIAL_PATTERN = /(?:SN\||SERIAL\||SN:)([A-Z0-9-]+)/i;

export type BarcodeCandidate = { sku: string; active?: boolean };

export type BarcodeParseResult<T extends BarcodeCandidate> =
  | { status: "empty" }
  | { status: "not-recognized"; code: string }
  | { status: "ok"; code: string; item: T; serial: string };

/**
 * Matches a scanned or typed code against the eligible, vendor-locked catalog for the selected
 * sub-category only, and pulls out an embedded serial number when the code carries one.
 * The longest matching SKU wins so one SKU being a prefix of another cannot pick the wrong item.
 */
export const parseBarcode = <T extends BarcodeCandidate>(rawCode: string, candidates: T[]): BarcodeParseResult<T> => {
  const code = (rawCode ?? "").trim();
  if (!code) return { status: "empty" };

  const upper = code.toUpperCase();
  const item = candidates
    .filter((candidate) => candidate.active !== false && candidate.sku && upper.includes(candidate.sku.toUpperCase()))
    .sort((a, b) => b.sku.length - a.sku.length)[0];
  if (!item) return { status: "not-recognized", code };

  const serial = code.match(EMBEDDED_SERIAL_PATTERN)?.[1]?.toUpperCase() ?? "";
  return { status: "ok", code, item, serial };
};

/** Format check for an embedded serial (6 to 20 letters, digits and dashes); empty string when valid. */
export const getScannedSerialError = (serial: string): string => getSerialNumberFormatError(serial);
